export interface ImageState {
  selectedImage: number;
  selectedImageIndex: number;
  isSelected: number;
  isHalfWidth: boolean;
  imageIcon: boolean;
  isSidebarOpen: boolean;
  selectedImageMessageId: string;
  hoverImageMessageId: string;
  // Inpainting state
  inpainting: {
    isActive: boolean;
    isEraserMode: boolean;
    brushSize: number;
    maskData: string;
  };
  // Local-upload inpainting mode
  isLocalInpaintingOpen: boolean;
  localUploadImageUrl: string;
}

export type ImageAction =
  | { type: 'SELECT_IMAGE'; payload: { index: number; messageId: string } }
  | { type: 'DESELECT_IMAGE' }
  | { type: 'HOVER_IMAGE'; payload: { index: number; hoverImageMessageId: string } }
  | { type: 'STOP_HOVER' }
  | { type: 'TOGGLE_EDIT_MODE' }
  | { type: 'SET_SIDEBAR_OPEN'; payload: { isOpen: boolean } }
  | { type: 'RESET_ALL' }
  | { type: 'SET_INPAINTING_ACTIVE'; payload: { isActive: boolean } }
  | { type: 'SET_ERASER_MODE'; payload: { isEraserMode: boolean } }
  | { type: 'SET_BRUSH_SIZE'; payload: { brushSize: number } }
  | { type: 'SET_MASK_DATA'; payload: { maskData: string } }
  | { type: 'CLEAR_INPAINTING' }
  | { type: 'OPEN_LOCAL_INPAINTING' }
  | { type: 'SET_LOCAL_UPLOAD_IMAGE'; payload: { url: string } };

export const initialImageState: ImageState = {
  selectedImage: -1,
  selectedImageIndex: -1,
  isSelected: -1,
  isHalfWidth: false,
  imageIcon: true,
  isSidebarOpen: false,
  selectedImageMessageId: '',
  hoverImageMessageId: '',
  inpainting: {
    isActive: false,
    isEraserMode: false,
    brushSize: 20,
    maskData: '',
  },
  isLocalInpaintingOpen: false,
  localUploadImageUrl: '',
};

export function imageReducer(state: ImageState, action: ImageAction): ImageState {
  switch (action.type) {
    case 'SELECT_IMAGE':
      return {
        ...state,
        selectedImage: action.payload.index,
        isSelected: action.payload.index,
        isHalfWidth: true,
        // Don't reset selectedImageIndex - keep hover state
        isSidebarOpen: false, // Auto-close sidebar when image is selected
        selectedImageMessageId: action.payload.messageId,
        isLocalInpaintingOpen: false, // Exit local-upload mode when selecting a chat image
        localUploadImageUrl: '', // Clear stale local blob URL
        inpainting: {
          ...state.inpainting,
          maskData: '', // Clear stale mask when switching to a different image
          isActive: false,
          isEraserMode: false,
        },
      };

    case 'DESELECT_IMAGE':
      return {
        ...state,
        selectedImage: -1,
        isSelected: -1,
        isHalfWidth: false,
        selectedImageIndex: -1,

        selectedImageMessageId : '',
        hoverImageMessageId: '',
        isLocalInpaintingOpen: false,
        localUploadImageUrl: '',

      };

    case 'HOVER_IMAGE':
      return {
        ...state,
        selectedImageIndex: action.payload.index,
        hoverImageMessageId : action.payload.hoverImageMessageId

      };

    case 'STOP_HOVER':
      return {
        ...state,
        selectedImageIndex: -1,
        hoverImageMessageId : ''
      };

    case 'TOGGLE_EDIT_MODE':
      return {
        ...state,
        imageIcon: !state.imageIcon,
        // When disabling edit mode, also close any open image viewer
        ...(state.imageIcon && {
          isHalfWidth: false,
          selectedImage: -1,
          isSelected: -1,
          selectedImageIndex: -1,
        }),
      };

    case 'SET_SIDEBAR_OPEN':
      return {
        ...state,
        isSidebarOpen: action.payload.isOpen,
        // Close image viewer when opening sidebar
        ...(action.payload.isOpen && {
          isHalfWidth: false,
          selectedImage: -1,
          isLocalInpaintingOpen: false, // Close local-upload mode when sidebar opens
          localUploadImageUrl: '', // Clear stale local blob URL
        }),
      };

    case 'RESET_ALL':
      return initialImageState;

    case 'SET_INPAINTING_ACTIVE':
      return {
        ...state,
        inpainting: {
          ...state.inpainting,
          isActive: action.payload.isActive,
          // Reset mask when disabling inpainting
          ...(action.payload.isActive === false && { maskData: '' })
        }
      };

    case 'SET_ERASER_MODE':
      return {
        ...state,
        inpainting: {
          ...state.inpainting,
          isEraserMode: action.payload.isEraserMode
        }
      };

    case 'SET_BRUSH_SIZE':
      return {
        ...state,
        inpainting: {
          ...state.inpainting,
          brushSize: action.payload.brushSize
        }
      };

    case 'SET_MASK_DATA':
      return {
        ...state,
        inpainting: {
          ...state.inpainting,
          maskData: action.payload.maskData
        }
      };

    case 'CLEAR_INPAINTING':
      return {
        ...state,
        inpainting: {
          ...state.inpainting,
          maskData: '',
          isActive: false,
          isEraserMode: false
        }
      };
    // Local-upload inpainting mode
    case 'OPEN_LOCAL_INPAINTING':
      return {
        ...state,
        isHalfWidth: true,
        selectedImage: -1,
        selectedImageIndex: -1,
        isSelected: -1,
        selectedImageMessageId: '',
        hoverImageMessageId: '',
        isSidebarOpen: false,
        isLocalInpaintingOpen: true,
        localUploadImageUrl: '',
        inpainting: {
          ...state.inpainting,
          isActive: false,
          isEraserMode: false,
          maskData: '',
        },
      };

    // Stores the local blob URL 
    case 'SET_LOCAL_UPLOAD_IMAGE':
      return {
        ...state,
        localUploadImageUrl: action.payload.url,
        inpainting: {
          ...state.inpainting,
          maskData: '', // Clear stale mask when selecting a new local image
          isActive: false,
          isEraserMode: false,
        },
      };
    default:
      return state;
  }
}
